/**
 * Small, dependency-free statistics helpers used by the CV metrics.
 * All functions are pure and never throw on empty input.
 */

export const mean = (values: readonly number[]): number => {
  if (values.length === 0) return 0;
  let sum = 0;
  for (const value of values) sum += value;
  return sum / values.length;
};

export const median = (values: readonly number[]): number => percentile(values, 0.5);

export const variance = (values: readonly number[]): number => {
  if (values.length === 0) return 0;
  const mu = mean(values);
  let sum = 0;
  for (const value of values) {
    const delta = value - mu;
    sum += delta * delta;
  }
  return sum / values.length;
};

export const stdDev = (values: readonly number[]): number => Math.sqrt(variance(values));

/**
 * Linear-interpolated percentile. `sorted` values are not required.
 * `p` is in [0, 1].
 */
export const percentile = (values: readonly number[], p: number): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const clamped = p <= 0 ? 0 : p >= 1 ? 1 : p;
  const position = clamped * (sorted.length - 1);
  const lower = Math.floor(position);
  const upper = Math.ceil(position);
  const lowerValue = sorted[lower] as number;
  if (lower === upper) return lowerValue;
  const upperValue = sorted[upper] as number;
  const fraction = position - lower;
  return lowerValue + (upperValue - lowerValue) * fraction;
};

/** Median absolute deviation, a robust spread estimate. */
export const medianAbsoluteDeviation = (values: readonly number[]): number => {
  if (values.length === 0) return 0;
  const mu = median(values);
  return median(values.map((value) => Math.abs(value - mu)));
};

export const minMax = (values: readonly number[]): { min: number; max: number } => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  if (!Number.isFinite(min)) return { min: 0, max: 0 };
  return { min, max };
};

/** Linear map of `value` from [inMin, inMax] to [0, 1], clamped. */
export const normalizeRange = (value: number, inMin: number, inMax: number): number => {
  if (inMax - inMin <= 1e-9) return 0;
  const t = (value - inMin) / (inMax - inMin);
  return t < 0 ? 0 : t > 1 ? 1 : t;
};

/** Rounds to `digits` decimals; used to keep reports byte-stable in tests. */
export const round = (value: number, digits = 3): number => {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

export const safeDiv = (a: number, b: number, fallback = 0): number =>
  Math.abs(b) < 1e-9 ? fallback : a / b;
